import type { ReactElement, FC } from 'react'
import React, { memo } from 'react'

import FunctionalPickerItem from '@vfuk/core-functional-picker-item'
import SimpleGrid from '@vfuk/core-simple-grid'
import ColorSwatch from '@vfuk/core-color-swatch'
import { getDataSelector } from '@vfuk/core-base-props'
import { useLocalTheme } from '@vfuk/core-themes'

import defaultTheme from './themes/ColorPicker.theme'
import * as Styled from './styles/ColorPicker.style'

import type { ColorPickerProps } from './ColorPicker.types'

const ColorPicker: FC<ColorPickerProps> = ({
  label,
  selected,
  groupName,
  inverse,
  onChange,
  onBlur,
  items,
  columns,
  id,
  dataSelectorPrefix,
  dataAttributes,
  localTheme,
  appearance = 'primary',
}: ColorPickerProps): ReactElement => {
  const componentName = 'ColorPicker'
  const colorPickerTheme = useLocalTheme(componentName, defaultTheme, localTheme)

  const { inverseColorSwatch, inversePickerItem } = inverse
    ? colorPickerTheme.appearance[appearance].inverse
    : colorPickerTheme.appearance[appearance]

  const defaultColumns = {
    sm: items.length <= 2 ? items.length : 3,
    md: items.length <= 4 ? items.length : 5,
  }

  const pickerType = Array.isArray(selected) ? 'checkbox' : 'radio'

  const handleOnBlur = (event: React.FocusEvent<HTMLFieldSetElement>): void => {
    if (onBlur) {
      onBlur(event)
    }
  }

  return (
    <Styled.Fieldset
      onBlur={handleOnBlur}
      id={id}
      data-selector={getDataSelector(dataSelectorPrefix)}
      data-component-name={componentName}
      {...dataAttributes}
    >
      <Styled.Legend data-selector={getDataSelector(dataSelectorPrefix, 'legend')}>{label}</Styled.Legend>
      <SimpleGrid columns={{ ...defaultColumns, ...columns }} spacing={2} verticalAlign='stretch'>
        {items?.map((pickerItem, index) => {
          const isSelected = pickerType === 'radio' ? selected === pickerItem.value : selected.includes(pickerItem.value)
          return (
            <Styled.PickerItemWrapper key={`${index + groupName}`}>
              <FunctionalPickerItem
                groupName={groupName}
                inverse={inversePickerItem}
                type={pickerType}
                onChange={onChange}
                value={pickerItem.value}
                title={pickerItem.title}
                id={pickerItem.id}
                state={pickerItem.state}
                key={index}
                srText={pickerItem.srText}
                checked={isSelected}
                dataSelectorPrefix={getDataSelector(dataSelectorPrefix, `item-${index}`)}
              >
                <Styled.ColorWrapper>
                  <ColorSwatch
                    key={`${pickerItem.id + groupName}`}
                    inverse={inverseColorSwatch}
                    size={3}
                    color={pickerItem.color}
                    dataSelectorPrefix={getDataSelector(dataSelectorPrefix, `color-swatch-${index}`)}
                  />
                </Styled.ColorWrapper>
              </FunctionalPickerItem>
            </Styled.PickerItemWrapper>
          )
        })}
      </SimpleGrid>
    </Styled.Fieldset>
  )
}

export default memo(ColorPicker)
